import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TableCell,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Badge,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Person as PersonIcon
} from '@mui/icons-material';
import TrackingBase from '../../components/TrackingBase/TrackingBase';
import LocationViewer from '../../components/LocationViewer/LocationViewer';
import { staffTrackingService } from '../../services/staffTrackingService';
import { devicesAPI } from '../../services/api';

export default function StaffTracking() {
  const [staff, setStaff] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationDialog, setLocationDialog] = useState({ open: false, staff: null });
  const [filters, setFilters] = useState({
    role: '',
    department: '',
    status: 'active'
  });
  
  // Estados para formulários
  const [formData, setFormData] = useState({
    staff_code: '',
    full_name: '',
    role: '',
    department: '',
    email: '',
    phone: '',
    access_level: 'basic',
    shift: 'morning',
    enable_zone_alerts: false,
    enable_productivity_tracking: false,
    panic_button_enabled: false
  });
  
  const [beaconData, setBeaconData] = useState({
    beacon_id: '',
    beacon_type: 'badge',
    has_panic_button: false
  });

  useEffect(() => {
    loadStaff();
    loadBadges();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualizar formData quando selectedItem mudar (para edição)
  useEffect(() => {
    // Este efeito será disparado pelo TrackingBase quando abrir dialogs
  }, []);

  const loadBadges = async () => {
    try {
      const response = await devicesAPI.getAll();
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      const badgesOnly = devicesData.filter(device => device.type === 'badge' && device.ble_id);
      setBadges(badgesOnly);
    } catch (error) {
      console.warn('Failed to load badges:', error.message);
      setBadges([]);
    }
  };

  const loadStaff = async () => {
    try {
      setLoading(true);
      const response = await staffTrackingService.getStaff(filters);
      setStaff(response.data || []);
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers para o componente base
  const handleCreateStaff = async (formData) => {
    try {
      return await staffTrackingService.createStaff(formData);
    } catch (error) {
      if (error.response?.status === 409) {
        throw new Error(`Matrícula ${formData.staff_code} já está em uso. Use outra matrícula.`);
      }
      throw error;
    }
  };

  const handleEditStaff = async (staffId, formData) => {
    return await staffTrackingService.updateStaff(staffId, formData);
  };

  const handleDeleteStaff = async (staffId) => {
    return await staffTrackingService.deleteStaff(staffId);
  };

  const handleAssociateBeacon = async (staffId, beaconData) => {
    return await staffTrackingService.associateBeacon(staffId, beaconData);
  };

  const handleViewLocation = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    if (staffMember) {
      setLocationDialog({ open: true, staff: staffMember });
    }
  };

  const handleCloseLocationDialog = () => {
    setLocationDialog({ open: false, staff: null });
  };



  // Renderizadores de formulário
  const renderCreateEditForm = (selectedItem, tabType, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        staff_code: formDataToSubmit.get('staff_code') || '',
        full_name: formDataToSubmit.get('full_name') || '',
        role: formDataToSubmit.get('role') || '',
        department: formDataToSubmit.get('department') || '',
        email: formDataToSubmit.get('email') || '',
        phone: formDataToSubmit.get('phone') || '',
        enable_zone_alerts: formDataToSubmit.get('enable_zone_alerts') === 'on',
        enable_productivity_tracking: formDataToSubmit.get('enable_productivity_tracking') === 'on',
        panic_button_enabled: formDataToSubmit.get('panic_button_enabled') === 'on'
      };
      onSubmit(data);
    };

    if (tabType === 'basic') {
      return (
        <form id="tracking-form" onSubmit={handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="staff_code"
                fullWidth
                label="Matrícula *"
                defaultValue={selectedItem?.staff_code || ''}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="full_name"
                fullWidth
                label="Nome Completo *"
                defaultValue={selectedItem?.full_name || ''}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Função *</InputLabel>
                <Select
                  name="role"
                  defaultValue={selectedItem?.role || ''}
                  label="Função *"
                >
                  {staffTrackingService.getRoles().map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="department"
                fullWidth
                label="Departamento"
                defaultValue={selectedItem?.department || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="email"
                fullWidth
                label="Email"
                type="email"
                defaultValue={selectedItem?.email || ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="phone"
                fullWidth
                label="Telefone"
                defaultValue={selectedItem?.phone || ''}
              />
            </Grid>
          </Grid>
        </form>
      );
    }

    if (tabType === 'rtls') {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Configurações de Rastreamento
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_zone_alerts"
                  defaultChecked={Boolean(selectedItem?.enable_zone_alerts)}
                />
              }
              label="Alertas de Zona"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="enable_productivity_tracking"
                  defaultChecked={Boolean(selectedItem?.enable_productivity_tracking)}
                />
              }
              label="Rastreamento de Produtividade"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="panic_button_enabled"
                  defaultChecked={Boolean(selectedItem?.panic_button_enabled)}
                />
              }
              label="Botão de Pânico (requer crachá com esta capacidade)"
            />
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  const renderBeaconForm = (selectedItem, availableDevices, onSubmit) => {
    const handleSubmit = (e) => {
      e.preventDefault();
      const formDataToSubmit = new FormData(e.target);
      const data = {
        beacon_id: formDataToSubmit.get('beacon_id') || '',
        beacon_type: 'badge',
        has_panic_button: false
      };
      onSubmit(data);
    };

    return (
      <form id="tracking-form" onSubmit={handleSubmit}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Crachá BLE</InputLabel>
              <Select
                name="beacon_id"
                defaultValue={selectedItem?.beacon_id || ''}
              >
                <MenuItem value="">
                  <em>Selecione um crachá</em>
                </MenuItem>
                {Array.isArray(availableDevices) && availableDevices.map((badge) => {
                  const capabilities = badge.capabilities || [];
                  const hasPanicButton = capabilities.includes('panic_button');
                  const hasAccelerometer = capabilities.includes('accelerometer');
                  return (
                    <MenuItem key={badge.id} value={badge.ble_id}>
                      <Box>
                        <Typography variant="body2">
                          {badge.ble_id} - {badge.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {hasPanicButton && 'Botão de pânico'}
                          {hasPanicButton && hasAccelerometer && ' • '}
                          {hasAccelerometer && 'Detecção de movimento'}
                          {!hasPanicButton && !hasAccelerometer && 'Localização apenas'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

        </Grid>
      </form>
    );
  };



  // Utilitários
  const getRoleColor = (role) => {
    const colors = {
      doctor: 'primary',
      nurse: 'success',
      technician: 'info',
      admin: 'warning',
      security: 'error',
      cleaning: 'default',
      maintenance: 'secondary'
    };
    return colors[role] || 'default';
  };

  const getRoleLabel = (role) => {
    const labels = {
      doctor: 'Médico',
      nurse: 'Enfermeiro',
      technician: 'Técnico',
      admin: 'Administrativo',
      security: 'Segurança',
      cleaning: 'Limpeza',
      maintenance: 'Manutenção'
    };
    return labels[role] || role;
  };

  // Métricas para o dashboard
  const getMetrics = (staff) => ({
    total: {
      label: 'Total Funcionários',
      value: staff.length,
    },
    active: {
      label: 'Ativos',
      value: staff.filter(s => s.employment_status === 'active').length,
      color: 'success.main',
    },
    online: {
      label: 'Online Agora',
      value: staff.filter(s => s.is_online && s.beacon_id).length,
      color: 'primary.main',
    },
    withBeacon: {
      label: 'Com Crachá',
      value: staff.filter(s => s.beacon_id).length,
      color: 'info.main',
    },
  });

  // Renderizadores para filtros
  const renderFilters = (filters, onFiltersChange) => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Função</InputLabel>
          <Select
            value={filters.role}
            label="Função"
            onChange={(e) => onFiltersChange({ ...filters, role: e.target.value })}
          >
            <MenuItem value="">Todas</MenuItem>
            {staffTrackingService.getRoles().map(role => (
              <MenuItem key={role.value} value={role.value}>
                {role.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          size="small"
          label="Departamento"
          value={filters.department}
          onChange={(e) => onFiltersChange({ ...filters, department: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormControl fullWidth size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status}
            label="Status"
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
          >
            {staffTrackingService.getEmploymentStatuses().map(status => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  // Renderizadores para o componente base
  const renderTableColumns = () => [
    <TableCell key="name">Nome</TableCell>,
    <TableCell key="code">Matrícula</TableCell>,
    <TableCell key="role">Função</TableCell>,
    <TableCell key="department">Departamento</TableCell>,
    <TableCell key="status">Status</TableCell>,
    <TableCell key="beacon">Crachá/Conexão</TableCell>,
  ];

  const renderTableRow = (staffMember) => [
    <TableCell key="name">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="action" />
        {staffMember.full_name}
      </Box>
    </TableCell>,
    <TableCell key="code">{staffMember.staff_code}</TableCell>,
    <TableCell key="role">
      <Chip
        label={getRoleLabel(staffMember.role)}
        color={getRoleColor(staffMember.role)}
        size="small"
      />
    </TableCell>,
    <TableCell key="department">{staffMember.department || '-'}</TableCell>,
    <TableCell key="status">
      <Chip
        label={staffMember.employment_status === 'active' ? 'Ativo' : 'Inativo'}
        color={staffMember.employment_status === 'active' ? 'success' : 'default'}
        size="small"
      />
    </TableCell>,
    <TableCell key="beacon">
      {staffMember.beacon_id ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Badge
            color={staffMember.is_online ? 'success' : 'error'}
            variant="dot"
          >
            <Chip 
              label={staffMember.is_online ? 'Online' : 'Offline'} 
              color={staffMember.is_online ? 'success' : 'warning'} 
              size="small" 
            />
          </Badge>
          <Typography variant="caption" color="textSecondary">
            {staffMember.beacon_id}
          </Typography>
        </Box>
      ) : (
        <Chip label="Sem crachá" color="default" size="small" />
      )}
    </TableCell>,
  ];

  return (
    <>
      <TrackingBase
        title="Rastreamento de Funcionários"
        data={staff}
        loading={loading}
        onRefresh={loadStaff}
        onCreateItem={handleCreateStaff}
        onEditItem={handleEditStaff}
        onDeleteItem={handleDeleteStaff}
        onAssociateBeacon={handleAssociateBeacon}
        onViewLocation={handleViewLocation}

        renderTableColumns={renderTableColumns}
        renderTableRow={renderTableRow}
        getMetrics={getMetrics}
        availableDevices={badges}
        filters={filters}
        onFiltersChange={setFilters}
        renderFilters={renderFilters}
        renderCreateEditForm={renderCreateEditForm}
        renderBeaconForm={renderBeaconForm}
      />
      
      <LocationViewer
        open={locationDialog.open}
        onClose={handleCloseLocationDialog}
        entity={locationDialog.staff}
        entityType="staff"
      />
    </>
  );
}